import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import 'dayjs/locale/cs';
import 'dayjs/locale/sk';
import 'dayjs/locale/hu';
import React from 'react';
import { currencyConfig, Language } from '@/constants/currencyConfig';

dayjs.extend(duration);

export type Currency = 'EUR' | 'CZK';

export function formatNum(
	amount?: number | string,
	options?: Intl.NumberFormatOptions,
	locales?: string | string[]
): string | null {
	if (amount === undefined) return null;
	try {
		const n = typeof amount === 'number' ? amount : parseFloat(amount);
		return new Intl.NumberFormat(locales, options).format(n);
	} catch (err) {
		return null;
	}
}

export function formatAmount(
	amount: number | string,
	lang: Language = 'cs',
	currency?: Currency
): React.JSX.Element {
	const n = typeof amount === 'number' ? amount : parseFloat(amount);

	const { locale, currency: defaultCurrency } =
		currencyConfig[lang as Language] ?? currencyConfig['cs'];

	const parts: Intl.NumberFormatPart[] = new Intl.NumberFormat(locale, {
		style: 'currency',
		currency: currency || defaultCurrency,
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).formatToParts(n);

	const fraction = parts.find((o) => o.type === 'fraction');
	const hasFraction = fraction && parseInt(fraction.value) > 0;

	return (
		<span>
			{parts.map((item, key) => {
				if (item.type === 'decimal' && !hasFraction) {
					return '';
				}
				if (item.type === 'fraction') {
					return hasFraction ? (
						<sup key={key} className="amountFraction">
							{item.value}
						</sup>
					) : (
						''
					);
				}
				return item.value;
			})}
		</span>
	);
}

export const formatDate = (date: Date, formatting: string, locale?: string): string => {
	return dayjs(date)
		.locale(locale || 'cs')
		.format(formatting);
};

export const formatPhoneNumber = (
	{ countryCode, number }: { countryCode?: string; number?: string } = {},
	{ spacedTriples, omitCountryCode }: { spacedTriples?: boolean; omitCountryCode?: boolean } = {}
): string => {
	if (number) {
		const parts: string[] = [];

		if (!omitCountryCode && countryCode) {
			parts.push(countryCode);
		}

		if (spacedTriples) {
			const triplets = number.match(/.{1,3}/g);
			if (triplets) {
				parts.push(triplets.join('\xA0'));
			}
		} else {
			parts.push(number);
		}

		return parts.join(spacedTriples ? '\xA0' : '');
	}

	return '';
};
